import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SelectInput from '@/components/General/inputs/SelectInput';

/**
 * VariableSelectInput — a SelectInput wrapper that appends a
 * "Trigger variables" group to the literal options. Selecting a variable
 * stores `{ var: key }` as the value instead of a literal.
 *
 * Props:
 *  - literalOptions: array of { value, label } — the existing options
 *  - variables: array of { key, label, type, semantic? } from the path-variables hook
 *  - paramDef: { type?, semantic?, multiple? } from ACTION_PARAM_DEFS
 *  - value: current value (literal, or { var: key }, or array for multiple)
 *  - onUpdate: callback
 *  - title, placeholder, helpText, disabled, required — passed through to SelectInput
 *  - testIDPrefix: optional prefix for testIDs
 *
 * Filtering: variables are already filtered server-side by paramType/paramSemantic
 * (the hook passes those filters). We just render what we receive.
 */
const VariableSelectInput = ({
    literalOptions = [],
    variables = [],
    paramDef = {},
    value,
    onUpdate,
    title,
    placeholder,
    helpText,
    disabled,
    required,
    testIDPrefix,
}) => {
    const { multiple } = paramDef;

    // Helper: is a value a binding object?
    const isBinding = (v) => v && typeof v === 'object' && !Array.isArray(v) && 'var' in v;

    // Build a lookup map for variable labels by key
    const varByKey = useMemo(() => {
        const map = {};
        for (const v of variables) {
            map[v.key] = v;
        }
        return map;
    }, [variables]);

    // Variable options for the dropdown (with icon)
    const variableOptions = useMemo(() =>
        variables.map(v => ({
            value: { var: v.key },
            label: v.label,
            icon: { web: 'flash' },
            testID: `variable-option-${v.key}`,
        })),
    [variables]);

    // Combined options: literals + separator + variables group
    const combinedOptions = useMemo(() => {
        if (!variableOptions.length) return literalOptions;
        return [
            ...literalOptions,
            { separator: true },
            ...variableOptions,
        ];
    }, [literalOptions, variableOptions]);

    // For display: resolve binding value to a label
    // SelectInput's internal display logic won't find binding objects in the
    // options list (the value is `{ var: key }` which matches the option value
    // `{ var: key }` by reference equality only). We need to help it.
    // We pass a "displayValue" that SelectInput can show.
    const displayValue = useMemo(() => {
        if (multiple) {
            // For multiple mode, value is an array. Each element may be literal or binding.
            if (!Array.isArray(value)) return value;
            // SelectInput handles multiple display internally (count + pills).
            // But pills need labels — we need to augment the options so labels resolve.
            return value;
        }
        if (isBinding(value)) {
            const varSpec = varByKey[value.var];
            if (varSpec) {
                // Return an object that SelectInput can use for display
                return { label: varSpec.label, value };
            }
            // Stale binding — return as-is; SelectInput won't find a label.
            // We'll show the warning below.
            return { label: value.var, value };
        }
        return value;
    }, [value, varByKey, multiple]);

    // Detect stale binding (key not in fetched spec)
    const staleBindingKey = useMemo(() => {
        if (multiple) {
            if (!Array.isArray(value)) return null;
            for (const el of value) {
                if (isBinding(el) && !varByKey[el.var]) return el.var;
            }
            return null;
        }
        if (isBinding(value) && !varByKey[value.var]) return value.var;
        return null;
    }, [value, varByKey, multiple]);

    // For multiple mode with bindings, we need to augment the combined options
    // so that SelectInput can resolve labels for binding elements in pills.
    // The variable options already have value={ var: key } which should match.
    // SelectInput compares with == so { var: key } == { var: key } is false
    // (object equality). We need a different approach for multiple.
    // Workaround: for multiple mode, convert binding values to a string key
    // for comparison, and convert back on select/deselect.
    const multipleValueStrings = useMemo(() => {
        if (!multiple || !Array.isArray(value)) return null;
        return value.map(v => isBinding(v) ? `var:${v.var}` : v);
    }, [value, multiple]);

    const multipleOptions = useMemo(() => {
        if (!multiple) return combinedOptions;
        // Convert variable option values to string keys for multiple mode
        return [
            ...literalOptions,
            { separator: true },
            ...variables.map(v => ({
                value: `var:${v.key}`,
                label: v.label,
                icon: { web: 'flash' },
                testID: `variable-option-${v.key}`,
            })),
        ];
    }, [multiple, combinedOptions, literalOptions, variables]);

    const handleMultipleUpdate = (newValues) => {
        // Convert string keys back to binding objects
        const result = newValues.map(v => {
            if (typeof v === 'string' && v.startsWith('var:')) {
                return { var: v.slice(4) };
            }
            return v;
        });
        onUpdate(result);
    };

    if (multiple) {
        return (
            <View>
                <SelectInput
                    title={title}
                    placeholder={placeholder}
                    helpText={helpText}
                    options={multipleOptions}
                    value={multipleValueStrings}
                    onUpdate={handleMultipleUpdate}
                    disabled={disabled}
                    multiple
                    required={required}
                />
                {staleBindingKey && (
                    <Text style={styles.staleWarning} testID={`${testIDPrefix || title}-stale-warning`}>
                        Variable no longer available: {staleBindingKey}
                    </Text>
                )}
            </View>
        );
    }

    return (
        <View>
            <SelectInput
                title={title}
                placeholder={placeholder}
                helpText={helpText}
                options={combinedOptions}
                value={displayValue}
                onUpdate={onUpdate}
                disabled={disabled}
                required={required}
            />
            {staleBindingKey && (
                <Text style={styles.staleWarning} testID={`${testIDPrefix || title}-stale-warning`}>
                    Variable no longer available: {staleBindingKey}
                </Text>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    staleWarning: {
        fontSize: 12,
        color: colors.error,
        fontStyle: 'italic',
        marginTop: 4,
        marginBottom: 8,
    },
}));

export default VariableSelectInput;
